// Hand-rolled SVG mini-charts for the analytics surfaces. No charting library —
// a slim horizontal bar and an uptime sparkline drawn with design tokens so they
// read as one system with the rest of the Studio. Both scale to their container.

/**
 * Slim horizontal fill (share of total, uptime %, …). Unlike ui.tsx's <Meter>,
 * a higher value is "better", so there's no warn/danger inversion — it's a quiet
 * accent bar. `pct` is clamped to 0–100.
 */
export function Bar({ pct, tone = 'accent' }: { pct: number; tone?: 'accent' | 'neutral' }) {
  const w = Math.max(0, Math.min(100, pct));
  return (
    <div className="a8-bar" aria-hidden>
      <span className={`a8-bar-fill ${tone}`} style={{ width: `${w}%` }} />
    </div>
  );
}

/**
 * Uptime-over-time sparkline. `points` is one value per day (0–100), with `null`
 * for days that have no data (drawn as a gap). The y-domain is derived from the
 * data (padded) so small day-to-day variation stays visible. Purely decorative
 * to screen readers — the accompanying table cell carries the numbers.
 */
export function Sparkline({
  points,
  width = 92,
  height = 26,
}: {
  points: Array<number | null>;
  width?: number;
  height?: number;
}) {
  const nums = points.filter((p): p is number => p != null);
  if (nums.length === 0) {
    return <span className="a8-spark-empty text-dim">—</span>;
  }
  const hi = 100;
  const lo = Math.max(0, Math.min(...nums) - 3);
  const span = hi - lo || 1;
  const pad = 2;
  const innerW = width - pad * 2;
  const innerH = height - pad * 2;
  const n = points.length;
  const x = (i: number) => pad + (n <= 1 ? innerW / 2 : (i / (n - 1)) * innerW);
  const y = (v: number) => pad + innerH - ((v - lo) / span) * innerH;

  // Break the polyline into segments so nulls become gaps rather than a dip to 0.
  const segments: string[] = [];
  let cur: string[] = [];
  points.forEach((p, i) => {
    if (p == null) {
      if (cur.length) segments.push(cur.join(' '));
      cur = [];
    } else {
      cur.push(`${x(i).toFixed(1)},${y(p).toFixed(1)}`);
    }
  });
  if (cur.length) segments.push(cur.join(' '));

  const last = points[n - 1];
  return (
    <svg
      className="a8-spark"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      fill="none"
      aria-hidden
    >
      {segments.map((pts, i) => (
        <polyline
          key={i}
          points={pts}
          stroke="var(--brand-accent)"
          strokeWidth={1.6}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
      {last != null && (
        <circle cx={x(n - 1)} cy={y(last)} r={2.1} fill="var(--brand-accent)" />
      )}
    </svg>
  );
}
